<script setup lang="ts">
// ========== IMPORTATIONS ==========
// Vue & Core
import {
  nextTick,
  onMounted,
  ref,
  toRaw,
  useTemplateRef,
  watch,
  watchEffect,
} from 'vue';

import { useRouter } from 'vue-router';
import {
  useStorage,
  useIntersectionObserver,
  useWindowSize,
} from '@vueuse/core';
import SeconDetails from './SeconDetails.vue';
// Vue Query
import { useMutation, useQuery } from '@tanstack/vue-query';
import {
  form_product_step_one,
  isFixedPositionTitle,
  isLoging,
  isTruncatedStyle,
  previewUrl,
  styleOfTimesIcon,
} from '@/refs/product.refs';
import { dataMenuHeaderNav } from '@/assets/svg/menuSimPrev';
import { formatPrice } from '@/utils/formatPrice';

const router = useRouter();
const { width, height } = useWindowSize();

const productId = useStorage('productId', '');

// État visuel
const isConfimedFavorie = ref(false);

const isVisibleOrNot = ref<boolean | null>(false);
const isDrawerVisible = useStorage('isDrawerVisible', false);
const currentPosition = useStorage<'closed' | 'half' | 'full'>(
  'currentPosition',
  'closed',
);
const lockedVal = useStorage('isLocked', false);
const isError = useStorage('isError', false);
const checkedvalue = ref();

// Gestion des messages
const errorMessage = useStorage('errorMessage', '');
const succesMessage = ref('');
const errorType = ref();
const errorStatus = ref();
const isSucceed = ref();

// Image de fond
const bg = ref({
  width: '100%',
  height: '280px',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
});
const isNotFavorite = ref(false);
// DOM Ref
const observateurIdRef = useTemplateRef<HTMLDivElement | null>(
  'observateurIdRef',
);

// Données utilisateur
const dataItemProfile = ref();

// ========== OBSERVATEUR ==========
useIntersectionObserver(
  observateurIdRef,
  ([{ isIntersecting }]) => {
    checkedvalue.value = isIntersecting;
    isVisibleOrNot.value = checkedvalue.value;
  },
  { threshold: 0.3 },
);
const isTruncated = ref(true);
const toggleDescription = () => {
  isTruncated.value = !isTruncated.value;
  isTruncatedStyle.value = !isTruncatedStyle.value;
};
</script>

<template>
  <div class="">
    <div style="font-size: 2rem"></div>

    <div>
      <keep-alive>
        <div
          class="relative w-full rounded-tl-[25px] rounded-tr-[25px] bg-white"
          id="heightContainer"
        >
          <div
            class="bg-softgray flex flex-row items-center justify-center"
            :style="bg"
            id="hidden"
          >
            <img
              v-if="previewUrl !== null"
              :src="previewUrl === null ? '' : previewUrl"
              :alt="''"
              height="250"
              width="250"
              class="object-contain"
              loading="lazy"
            />
            <p v-else>Ajpouter une image</p>
          </div>
          <div class="h-px bg-white" ref="observateurIdRef"></div>
          <div
            v-show="isFixedPositionTitle"
            class="detailsContainerBottom gap-4px relative top-0 bottom-0 w-full bg-white px-[16px] pb-0"
            :class="isVisibleOrNot ? 'px-[16px]' : 'px-[16px]'"
          >
            <div
              class="firstContainerBottomDetail z-30 w-full"
              :class="
                isVisibleOrNot
                  ? ''
                  : 'relative top-0 z-4000 flex h-20 w-full items-center bg-white py-4 shadow-none backdrop-blur-md'
              "
            >
              <div class="w-full">
                <div class="invisible h-[16px] bg-black" />
                <div class="flex w-full flex-row items-start justify-between">
                  <div class="w-full pr-8">
                    <div
                      class="flex w-full flex-row items-center justify-between"
                    >
                      <p
                        class="text-20px font-tt-semiBold-Normal text-grand-text m-0"
                      >
                        {{
                          form_product_step_one.designation
                            ? form_product_step_one.designation
                            : 'Ajouter un titre'
                        }}
                      </p>
                    </div>
                  </div>

                  <div
                    class="LefticonMenusss bg-soft-gray"
                    :class="{
                      flex: isLoging,
                      hidden: !(isVisibleOrNot && isLoging),
                    }"
                  >
                    <div class="m-0 flex w-full items-center justify-center">
                      <div
                        v-if="!Boolean('false')"
                        class="relative flex size-[50px] items-center justify-center bg-none hover:bg-white"
                        :class="isVisibleOrNot ? 'flex' : 'hidden'"
                      >
                        <div
                          class="bg-black/0.1 absolute size-[30px] rounded-full"
                        />

                        <div
                          :class="!isLoging ? 'hidden' : 'flex'"
                          class="items-center justify-center"
                          v-html="dataMenuHeaderNav[3]?.link"
                        />
                      </div>

                      <div
                        v-else
                        @mousedown.stop
                        @mouseup.stop
                        @touchstart.stop
                        @touchend.stop
                        @wheel.stop
                        @scroll.stop
                        class="relative flex size-[50px] items-center justify-center hover:bg-white"
                        :class="isVisibleOrNot ? 'flex' : 'hidden'"
                      >
                        <div
                          class="bg-black/0.1 absolute size-[30px] rounded-full"
                          @mousedown.stop
                          @mouseup.stop
                          @touchstart.stop
                          @touchend.stop
                          @wheel.stop
                          @scroll.stop
                        />
                        <div
                          class="flex items-center justify-center"
                          v-html="dataMenuHeaderNav[8]?.link"
                        />
                      </div>
                    </div>

                    <i
                      class="pi pi-times text-text-newUpdateBigTexte transition-all delay-200 duration-300 ease-in-out"
                      style="font-size: 1rem"
                      :class="isVisibleOrNot ? 'hidden' : 'visible'"
                    ></i>
                  </div>
                </div>
                <div class="invisible h-1 bg-black" />
                <p class="text-20px font-tt-medium-Normal text-grand-text m-0">
                  {{
                    formatPrice(
                      Number(
                        form_product_step_one?.price
                          ? form_product_step_one?.price
                          : 0,
                      ),
                    )
                  }}
                </p>
              </div>
            </div>
            <div class="h-1 bg-black" />
            <div class="w-full">
              <p
                v-if="isTruncatedStyle"
                ref="descRef"
                :class="!isTruncatedStyle ? 'line-clamp-0' : 'line-clamp-2'"
                class="font-tt-normal-Normal text-12px text-secondary-text m-0 line-clamp-2 max-w-full bg-white leading-[15px] text-ellipsis"
              >
                {{
                  form_product_step_one?.description === '' ||
                  form_product_step_one?.description === null
                    ? 'Aucune description pour ce produit'
                    : form_product_step_one?.description
                }}
              </p>

              <p
                v-else
                class="text-littleSize text-text-newTextSecondary m-0 w-full bg-white leading-[15px]"
              >
                {{
                  form_product_step_one?.description === '' ||
                  form_product_step_one?.description === null
                    ? 'Aucune description pour ce produit'
                    : form_product_step_one?.description
                }}
              </p>
            </div>

            <div
              @mousedown.stop
              @mouseup.stop
              @touchstart.stop
              @touchend.stop
              @wheel.stop
              @scroll.stop
              @click="toggleDescription"
            >
              <p
                v-show="isTruncated"
                class="text-12px text-selection font-tt-medium-Normal m-0 line-clamp-2 max-w-full cursor-pointer"
              >
                {{ isTruncated ? 'Lire plus' : 'Lire moins' }}
              </p>
            </div>
          </div>
        </div>
      </keep-alive>
    </div>

    <SeconDetails />
  </div>
</template>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');
@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
.slide-up-drawer-enter-active,
.slide-up-drawer-leave-active {
  transition:
    transform 0.4s cubic-bezier(0.25, 0.8, 0.5, 1),
    opacity 0.4s ease;
}

.slide-up-drawer-enter-from,
.slide-up-drawer-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
.bgLayer {
  background-image: url('http://res.cloudinary.com/nextepsol/image/upload/v1742218957/epay_assets/food_bg_mqfur.png');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-color: white;
}
dialog {
  transform: translateY(100%);
  opacity: 0;
  transition:
    transform 0.4s ease,
    opacity 0.4s ease;
  border: none;
  background: white;
  border-radius: 8px;
  padding: 20px;
  scrollbar-width: none;
}

dialog.open {
  animation: slideUp 0.4s forwards;
}
.pi-spinner {
  position: absolute;
  top: 50%;
  left: 47%;
  transform: translate(-50%, -50%);
}
.contain {
  width: 100%;
}
.SILhI {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: start;
  width: 100%;
  color: #070707;
}
.HidenC {
  display: none;
}
.LefticonMenusss {
  cursor: pointer;

  border-radius: 52%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  box-shadow: 10px #fbf8f2;
  width: 38px;
  height: 38px;
  padding: 5%;
  position: realative;
}
.FirstDateCont p span {
  color: #111111;
  font-size: 0.625rem;
  font-family: 'Poppins';
  font-style: normal;
}
.Promotion {
  position: '';
  bottom: 1.25rem;
}

.folowing {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  gap: 0.625rem;
}
.folowing p,
.folowing p span {
  font-family: 'Poppins';
  font-weight: 600;
  font-size: 0.75rem;
  color: #4b4b4a;
}

.hidden {
  display: none;
}
.hiddenFisrt {
  display: none;
}
.visible {
  display: block;
}
.visibleSecond {
  display: block;
}
.active {
  color: #070707;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  width: 2.25rem; /* 36px -> 2.25rem */
  height: 2.25rem; /* 36px -> 2.25rem */
  border-radius: 0.6875rem; /* 11px -> 0.6875rem */
}
.bg {
  background: linear-gradient(135deg, #333, #111);
  color: #fbf8f2;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  width: 2.25rem; /* 36px -> 2.25rem */
  height: 2.25rem; /* 36px -> 2.25rem */
  border-radius: 0.6875rem; /* 11px -> 0.6875rem */
}

.ThirdContainer {
  max-width: 100%;
}
.ThirdContainerBottomDetails {
  background-color: #fbf8f2;
  max-width: 100%;
  display: flex;
  flex-direction: column;
}
.FirstDateCont {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
}
.FirstDateCont P {
  font-family: Poppins;
  font-style: normal;
  font-size: 0.75rem; /* 12px -> 0.75rem */
}

.details {
  padding: 0%;
}

/* .detailsImageFirstContainer {
  background-image: url('/Images/bgSLug.png');
  background-repeat: no-repeat;
  background-size: cover;
  width: 100%;
  background-position: center center;
} */

.detailsContainerBottom {
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  align-content: start;
  justify-items: start;
  background-color: #fbf8f2;
  border-top-left-radius: 1.5625rem; /* 25px -> 1.5625rem */
  border-top-right-radius: 1.5625rem; /* 25px -> 1.5625rem */

  max-width: 100%;
}

.SecondContainerBottomDetails {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: start;
  width: 100%;
  color: #070707;
}

.firstDiv {
  display: flex;
  flex-direction: row;
  justify-items: space-between;
  align-items: start;
  line-height: 1.75rem; /* 28px -> 1.75rem */
  height: 7.5rem; /* 120px -> 7.5rem */
  width: 100%;
}
.firstDiv p,
.description {
  font-weight: 600;
  font-size: 1rem; /* 16px -> 1rem */
  font-family: 'Poppins';
  color: #4b4b4a;
}
.firstDiv p span {
  font-size: 0.875rem; /* 14px -> 0.875rem */
  font-family: 'Poppins';
  font-weight: 500;
  color: #4b4b4a;
}

.firstContainerBottomDetails .secondDiv p {
  font-family: 'Poppins';
  font-size: 1rem; /* 16px -> 1rem */
  font-weight: 500;
  color: #4b4b4a;
}
.paragrapheContainer {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  width: 2.25rem; /* 36px -> 2.25rem */
  height: 2.25rem; /* 36px -> 2.25rem */
  background: linear-gradient(135deg, #333, #111);
  color: #fbf8f2;
  border-radius: 0.6875rem; /* 11px -> 0.6875rem */
}
.SecondContainerBottomDetails {
  width: 100%;
  position: '';
  bottom: 1.25rem; /* 20px -> 1.25rem */
}
.SecondContainerBottomDetails .firstContainerOfSecondContainerBottomDetails {
  display: flex;
  width: 11.375rem; /* 180px -> 11.375rem */
  height: 1.9875rem; /* 31.79px -> 1.9875rem */
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  justify-content: center;
  align-items: center;
  align-content: center;
  gap: 0.1875rem; /* 3px -> 0.1875rem */
  position: '';
  right: 0.4375rem; /* 7px -> 0.4375rem */
}
.SecondContainerBottomDetails
  .firstContainerOfSecondContainerBottomDetails
  div {
  display: flex;
  background-position: center center;
  justify-content: center;
  align-items: center;
  align-content: center;
  position: '';
  right: 1.25rem; /* 20px -> 1.25rem */
}

.SecondContainerBottomDetails .SecondContainerOfSecondContainerBottomDetails {
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
  justify-content: center;
  background-color: #fbda7a;
  gap: 0.625rem; /* 10px -> 0.625rem */
  width: 6.4375rem; /* 103px -> 6.4375rem */
  height: 2rem; /* 32px -> 2rem */
  border-radius: 0.3125rem; /* 5px -> 0.3125rem */
}
.PARA {
  font-size: 0.75rem; /* 12px -> 0.75rem */
  font-style: normal;
  color: #fbf8f2;
  font-weight: 300;
  font-family: 'Poppins';
}
.SecondContainerBottomDetails .SecondContainerOfSecondContainerBottomDetails p {
  color: #111111;
  font-family: 'Poppins';
  font-size: 0.75rem; /* 12px -> 0.75rem */
}
.paragrapheContainer span {
  font-size: 1.875rem; /* 30px -> 1.875rem */
}
.paragrapheContainer:hover {
  cursor: pointer;
}
.SecondContainer {
  max-width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  background-color: inherit;
  background-color: #ffffff;
  padding-right: 5%;
  padding-left: 5%;
  height: 2.5rem; /* 40px -> 2.5rem */
}

.SecondContainer .FisrtsSecond {
  color: #4b4b4a;
  font-family: 'Poppins';
  font-size: 1rem; /* 16px -> 1rem */
  font-weight: 500;
  line-height: 1.1875rem; /* 19px -> 1.1875rem */
}

.SecondContainer .FisrtsSecond span {
  color: #707070;
  font-family: 'Poppins';
  font-size: 0.75rem; /* 12px -> 0.75rem */
  font-style: normal;
  font-weight: 300;
}

.SECONDs {
  font-family: 'Poppins';
  font-style: normal;
  font-size: 0.75rem; /* 12px -> 0.75rem */
  color: #707070;
}
.SECONDs span {
  visibility: hidden;
}
.ThirdContainer {
  max-width: 100%;
}
.ThirdContainer .ThirdContainerBottomDetails {
  background-color: #fbf8f2;
  max-width: 100%;
}
.FirstDateCont {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
}
.FirstDateCont P {
  font-family: Poppins;
  font-style: normal;
  font-size: 0.75rem; /* 12px -> 0.75rem */
}
.firstContainerBottomDetail {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: start;
  align-content: start;
  height: auto;
}
.secondDivDetailBottom {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  justify-items: center;
  gap: 0.3125rem; /* 5px -> 0.3125rem */
  border: 1px solid #707070;
  width: 7.125rem; /* 114px -> 7.125rem */
  height: 2.25rem; /* 36px -> 2.25rem */
  border-radius: 0.75rem; /* 12px -> 0.75rem */
  padding: 0.0625rem; /* 1px -> 0.0625rem */
}

@media (min-width: 375px) {
  .firstDiv {
    height: 8.125rem; /* 130px -> 8.125rem */
  }
  .firstDiv p {
    font-size: 1.5625rem; /* 25px -> 1.5625rem */
  }
  .firstDiv p span {
    font-size: 1rem; /* 16px -> 1rem */
  }

  .SecondContainerBottomDetails {
    width: 100%;
    position: '';
    bottom: 0px;
  }
  .SecondContainerBottomDetails .firstContainerOfSecondContainerBottomDetails {
    width: 12.875rem; /* 206px -> 12.875rem */
    height: 1.9875rem; /* 31.79px -> 1.9875rem */
  }
  .SecondContainerBottomDetails .SecondContainerOfSecondContainerBottomDetails {
    gap: 0.625rem; /* 10px -> 0.625rem */
    width: 6.4375rem; /* 103px -> 6.4375rem */
    height: 2rem; /* 32px -> 2rem */
  }
  .detailsImageFirstContainer {
    background-size: 29.375rem 25.1875rem; /* 470px 403px -> 29.375rem 25.1875rem */
    background-position: -4.0625rem -7.5rem; /* -65px -120px -> -4.0625rem -7.5rem */
  }
}

@media (min-width: 425px) {
  .firstDiv {
    height: 8.125rem; /* 130px -> 8.125rem */
  }
  .firstDiv p {
    font-size: 1.5625rem; /* 25px -> 1.5625rem */
  }
  .firstDiv p span {
    font-size: 1rem; /* 16px -> 1rem */
  }
  .SecondContainerBottomDetails {
    width: 100%;
    position: '';
    bottom: 0px;
  }
  .SecondContainerBottomDetails .firstContainerOfSecondContainerBottomDetails {
    width: 12.875rem; /* 206px -> 12.875rem */
    height: 1.9875rem; /* 31.79px -> 1.9875rem */
  }
  .SecondContainerBottomDetails .SecondContainerOfSecondContainerBottomDetails {
    gap: 0.625rem; /* 10px -> 0.625rem */
    width: 6.4375rem; /* 103px -> 6.4375rem */
    height: 2rem; /* 32px -> 2rem */
  }
  .detailsImageFirstContainer {
    background-size: 33.25rem 25.1875rem; /* 532px 403px -> 33.25rem 25.1875rem */
    background-position: -4.375rem -7.5rem; /* -70px -120px -> -4.375rem -7.5rem */
  }
}

.PromotionIcon {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.625rem; /* 10px -> 0.625rem */
}
.Secondf {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: start;
  width: 100%;
  color: #070707;
}
.sil {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
  justify-content: center;
  background-color: #fbda7a;
  height: 1.875rem; /* 30px -> 1.875rem */
  border-radius: 0.3125rem; /* 5px -> 0.3125rem */
}
.sil p {
  color: #111111;
  font-family: 'Poppins';
  font-size: 0.75rem; /* 12px -> 0.75rem */
}
.STRING {
  font-family: 'Poppins';
  font-size: 0.75rem; /* 12px -> 0.75rem */
  font-display: 'normal';
  color: #707070;
}
.HidenClass {
  display: none;
}
</style>
